import { useRef, useEffect, useContext } from "react";
import isEqual from "lodash/isEqual";
import { TrackingContext } from "./TrackingContext";
import { trackFilterEvent } from "./mixpanelTracker";
import { getBaseUrlParams } from "../../utils/getBaseUrlParams";

const EVENT_NAME = "Filter Option Changed";

// Normalises a selection (array or single value) to a sorted list of value-keys for comparison.
const selectionKeys = (selection, valueKey) =>
  (Array.isArray(selection) ? selection : [selection])
    .filter((item) => item !== undefined && item !== null)
    .map((item) => (item && typeof item === "object" ? item[valueKey] : item))
    .sort();

const toDisplayName = (value, labelKey, isDateRange = false) => {
  const name = typeof value === "object" && value !== null ? value[labelKey] : value;
  const str = String(name ?? "").trim();
  return isDateRange ? str.split(" | ")[0].trim() : str;
};

const buildOptions = (selected, labelKey, isDateRange = false) => {
  const list = Array.isArray(selected) ? selected : [selected];
  return list
    .filter((v) => v !== undefined && v !== null)
    .map((v) => toDisplayName(v, labelKey, isDateRange));
};

// Filter tracking for a dropdown/toggle. Inert unless rendered inside an enabled TrackingContext.
// Trigger by filter type: hardcoded fires on change (with options); backend — detected by
// `includeSelectAll`, or by the option list changing after mount — fires once on close (no options),
// and only when the selection actually changed while open.
const useFilterTracking = ({
  options,
  includeSelectAll,
  filterName,
  moduleName,
  labelKey = "name",
  selected,
  valueKey = "id"
}) => {
  const { pageName, enabled } = useContext(TrackingContext);
  const containerRef = useRef(null);
  const resolvedNameRef = useRef(null);
  const mountedRef = useRef(false);
  const isDynamicRef = useRef(Boolean(includeSelectAll));
  const changedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    isDynamicRef.current = true;
  }, [options, enabled]);

  // Walk up to 3 ancestor levels looking for a p/label/h2 previous sibling.
  // Needed because wrappers vary: some components sit directly after the label,
  // others have 1–2 intervening divs (e.g. toggles, breakdown/heatmap dropdowns).
  const findFilterLabel = () => {
    const LABEL_TAGS = ["P", "LABEL", "H2"];
    let node = containerRef.current;
    for (let i = 0; i < 3; i++) {
      const sibling = node?.previousElementSibling;
      if (sibling && LABEL_TAGS.includes(sibling.tagName)) {
        return sibling.textContent.trim() || null;
      }
      node = node?.parentElement;
    }
    if (containerRef.current?.closest(".react-section-title")) return "View";
    return null;
  };

  // Cache only a real name so a transient miss (dropdown still behind a loader) can re-resolve.
  const resolveFilterName = () => {
    if (resolvedNameRef.current) return resolvedNameRef.current;
    const name = filterName || findFilterLabel();
    if (name) resolvedNameRef.current = name;
    return name || "View";
  };

  const resolveModuleName = () =>
    moduleName || containerRef.current?.closest(".react-section-wrapper")?.dataset.testid || null;

  const baseProps = () => ({
    page_name: pageName || null,
    module_name: resolveModuleName(),
    filter_name: resolveFilterName(),
    // The org being viewed, read from the URL the same way every other api/v2 call does. Admins can
    // open any org's dashboard, so this — not the id on the user record — is what the event describes.
    // The backend authorizes it before trusting it: it is a hint, not a grant.
    organization_id: getBaseUrlParams().organization_id,
    // Mixpanel's built-in URL property — full URL incl. host → identifies which instance the user is on.
    "$current_url": window.location.href
  });

  const emit = (options) => {
    if (!enabled) return;
    trackFilterEvent(EVENT_NAME, options ? { ...baseProps(), options } : baseProps());
  };

  const trackToggle = (state) => emit([state]);

  const onClose = () => {
    if (!isDynamicRef.current || !changedRef.current) return;
    changedRef.current = false;
    emit();
  };

  const trackChange = (value) => {
    if (!enabled) return;
    // Ignore no-op changes (re-picking the same option, or Picky re-emitting the current value).
    if (isEqual(selectionKeys(value, valueKey), selectionKeys(selected, valueKey))) return;
    if (isDynamicRef.current) {
      changedRef.current = true;
      return;
    }
    const isDateRange = /date range/i.test(resolveFilterName() || "");
    emit(buildOptions(value, labelKey, isDateRange));
  };

  return { containerRef, onClose, trackChange, trackToggle };
};

export default useFilterTracking;
